import { Component, ElementRef, Inject, OnInit, Renderer2 } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { ConfirmationDialogueComponent } from 'app/components/common/confirmation-dialogue/confirmation-dialogue.component';
import { IAtpCourse } from 'app/models/atp/atp-course';
import { IAtpCourseStudent } from 'app/models/atp/atp-course-student';
import { IAddress } from 'app/models/common/address';
import { IPersonFilter } from 'app/models/filters/person-filter';
import { IPerson } from 'app/models/people/person';
import { IPersonAddress } from 'app/models/people/person-address';
import { AtpService } from 'app/services/atp/atp.service';
import { CommonService } from 'app/services/common/common.service';
import { SharedService } from 'app/services/core/shared.service';
import { PersonService } from 'app/services/person/person.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AtpComponent } from '../atp.component';
import { AtpEditCourseComponent } from '../atp-edit-course/atp-edit-course.component';

@Component({
  selector: 'app-atp-edit-course-student',
  templateUrl: './atp-edit-course-student.component.html',
  styleUrls: ['./atp-edit-course-student.component.scss']
})
export class AtpEditCourseStudentComponent implements OnInit {
  public courseStudentDataForm = null;
  public courseStudentData: IAtpCourseStudent = null;  
  public address: IAddress = {id: 0, state: '', street: '', city: '', contactType: '', county: '', zip: ''};
  public studentAddress: IPersonAddress = {id: 0, person: null, address: this.address, isCurrentMailing: true, isCurrentPhysical: false}  
  public uiData = LEAD_BASED_PAINT;  
  
  public studentList: IPerson[] = [];
  public filteredStudentList: IPerson[] = [];
  public selectedStudent: IPerson = {id: 0, firstName: '', lastName: '', middleName: '', title: '', ssn: '', dateOfBirth: '', inactive: false, response: 'Unk', certificates: null};  
  public personDataForm = null;
  public studentFilter: IPersonFilter = {filterAffiliation: [], filterStatus: '', filterString: '', expirationStartDate: '', expirationEndDate: '', filterDiscipline: []};
  public status: string = 'Pending'; // Default to Pending
  public isProcessed: boolean = false;

  constructor(private readonly personService:PersonService,
    @Inject(MAT_DIALOG_DATA) public data:{student:IAtpCourseStudent, course:IAtpCourse, courseStudents:IAtpCourseStudent[], studentId: number, status?: string},
    public sharedService: SharedService,
    private readonly atpService: AtpService,
    private readonly commonService: CommonService,
    private readonly toastr: ToastrService,   
    public dialog: MatDialog, 
    private readonly dialogRef: MatDialogRef<AtpEditCourseStudentComponent>,
    ) 
  {     
    this.data.studentId = this.data.studentId || 0;
    this.status = this.data.status || 'Pending';
    this.isProcessed = this.status === 'Processed';
  }
  
  ngOnInit(): void {
    this.setCourseDataFormFields();
    if(this.data.student !== null && this.data.student !== undefined){
      if(this.isProcessed && this.data.student['student']) {
        // For Processed students from table
        this.studentList = [this.data.student['student']];
        this.filteredStudentList = this.studentList;
        this.selectedStudent = this.data.student['student'];
      } else if(!this.isProcessed) {
        // For Pending students - create a temporary IPerson from the flat structure
        this.selectedStudent = {
          id: 0,
          firstName: this.data.student['firstName'] || '',
          lastName: this.data.student['lastName'] || '',
          middleName: '',
          title: '',
          ssn: '',
          dateOfBirth: this.data.student['dateOfBirth'] || '',
          inactive: false,
          response: 'Unk',
          certificates: null
        };
        this.studentList = [this.selectedStudent];
        this.filteredStudentList = this.studentList;
      }
      this.loadData();
    }
    else if(this.data.studentId > 0){            
      // Load by ID using appropriate service
      const studentService$ = this.isProcessed 
        ? this.atpService.getATPCourseStudentById(this.data.studentId)
        : this.atpService.getATPPendingCourseStudentById(this.data.studentId);
        
      studentService$.subscribe(result=>{        
        this.data.student = result;
        this.data.course = result.atpCourse;
        
        if(this.isProcessed && result['student']) {
          this.studentList = [result['student']];
          this.selectedStudent = result['student'];
        } else if(!this.isProcessed) {
          this.selectedStudent = {
            id: 0,
            firstName: result['firstName'] || '',
            lastName: result['lastName'] || '',
            middleName: '',
            title: '',
            ssn: '',
            dateOfBirth: result['dateOfBirth'] || '',
            inactive: false,
            response: 'Unk',
            certificates: null
          };
          this.studentList = [this.selectedStudent];
        }
        this.filteredStudentList = this.studentList;          
        this.loadData();
      },error=>{this.toastr.error("There was an error getting the course student: ", error);        
        this.loadData();
      })      
    }
    else{
      // For new students (Add Student), default to Pending and ensure isProcessed is false
      this.status = 'Pending';
      this.isProcessed = false;
      this.loadData();
    }    
  }

  loadData(){
    if(this.selectedStudent?.id >= 0){
      if(this.data.student !== null){
        this.courseStudentData = this.data.student; 
        if(this.isProcessed && this.data.student['student']) {
          this.courseStudentData['student'] = this.selectedStudent;
        }
      }
      else{
        this.courseStudentData = {
          id: 0,
          atpCourse: this.data.course,
          student: this.isProcessed ? this.selectedStudent : null,
          courseCertificateNumber: "",
          testScore: null,
          photoImage: "",
          paymentUpdate: true
        } as any;     
      }
  
      // Load address only for Processed students
      if(this.isProcessed && this.selectedStudent.id > 0) {
        this.personService.getPersonAddressesByPersonId(this.selectedStudent.id).subscribe(result=>{
          this.studentAddress = result.length > 0 ? result[0] : {id: 0, person: null, address: null, isCurrentMailing: true, isCurrentPhysical: false};
          this.address = this.studentAddress.id > 0 ? this.studentAddress.address : {id: 0, state: '', street: '', city: '', contactType: '', county: '', zip: ''};
          for (const controlName in this.studentAddress?.address) {
            if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {        
              this.courseStudentDataForm.controls[controlName].setValue(this.studentAddress.address[controlName]);          
            }
          }  
        },error=>{
          this.toastr.error("There was an error getting the student address: ", error);
        })
      } else if(!this.isProcessed) {
        // For Pending students, load from flat structure
        if(this.data.student) {
          this.courseStudentDataForm.controls['street'].setValue(this.data.student['street'] || '');
          this.courseStudentDataForm.controls['city'].setValue(this.data.student['city'] || '');
          this.courseStudentDataForm.controls['state'].setValue(this.data.student['state'] || 'OR');
          this.courseStudentDataForm.controls['zip'].setValue(this.data.student['zip'] || '');
        }
      }
  
      for (const controlName in this.courseStudentData) {
        if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {        
          this.courseStudentDataForm.controls[controlName].setValue(this.courseStudentData[controlName]);          
        }
      }  
  
      // Load student fields
      if(this.isProcessed && this.courseStudentData['student']) {
        for (const controlName in this.courseStudentData['student']) {
          if (this.courseStudentDataForm.controls.hasOwnProperty(controlName) && this.courseStudentData['student'].id !== 0) {        
            this.courseStudentDataForm.controls[controlName].setValue(this.courseStudentData['student'][controlName]);          
          }
        }
      } else if(!this.isProcessed && this.data.student) {
        // For Pending, load from flat structure
        this.courseStudentDataForm.controls['firstName'].setValue(this.data.student['firstName'] || '');
        this.courseStudentDataForm.controls['lastName'].setValue(this.data.student['lastName'] || '');
        this.courseStudentDataForm.controls['dateOfBirth'].setValue(this.data.student['dateOfBirth'] || null);
      }
      
      // Apply disabled state for Processed students
      if(this.isProcessed) {
        this.courseStudentDataForm.controls['firstName'].disable();
        this.courseStudentDataForm.controls['lastName'].disable();
        this.courseStudentDataForm.controls['dateOfBirth'].disable();
        this.courseStudentDataForm.controls['street'].disable();
        this.courseStudentDataForm.controls['city'].disable();
        this.courseStudentDataForm.controls['state'].disable();
        this.courseStudentDataForm.controls['zip'].disable();
      }
    }        
  }

  setCourseDataFormFields(){       
    this.courseStudentDataForm = new UntypedFormGroup({
      courseCertificateNumber: new UntypedFormControl(""),
      testScore: new UntypedFormControl(""),
      photoImage: new UntypedFormControl(""),
      firstName: new UntypedFormControl(""),
      lastName: new UntypedFormControl(""),
      dateOfBirth: new UntypedFormControl(null),
      street: new UntypedFormControl(""),
      city: new UntypedFormControl(""),
      state: new UntypedFormControl(""),
      zip: new UntypedFormControl(""),
    }); 
  }

  updateCourseStudentData(): void {    
    for (const controlName in this.courseStudentData) {
      if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {
        this.courseStudentData[controlName] = this.courseStudentDataForm.controls[controlName].value;        
      }
    } 

    if(this.isProcessed) {
      // For Processed students, update the student object
      for (const controlName in this.selectedStudent) {
        if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {
          this.selectedStudent[controlName] = this.courseStudentDataForm.controls[controlName].value;        
        }
      }    

      for (const controlName in this.address) {
        if (this.courseStudentDataForm.controls.hasOwnProperty(controlName)) {
          this.address[controlName] = this.courseStudentDataForm.controls[controlName].value;        
        }
      }
    } else {
      // For Pending students, update flat structure
      this.courseStudentData['firstName'] = this.courseStudentDataForm.controls['firstName'].value;
      this.courseStudentData['lastName'] = this.courseStudentDataForm.controls['lastName'].value;
      this.courseStudentData['dateOfBirth'] = this.courseStudentDataForm.controls['dateOfBirth'].value;
      this.courseStudentData['street'] = this.courseStudentDataForm.controls['street'].value;
      this.courseStudentData['city'] = this.courseStudentDataForm.controls['city'].value;
      this.courseStudentData['state'] = this.courseStudentDataForm.controls['state'].value;
      this.courseStudentData['zip'] = this.courseStudentDataForm.controls['zip'].value;
    }
  }
  
  savePrompt(){
    let isNew = this.courseStudentData.id === 0;
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',      
      data: {message: isNew ? 'Are you sure you want to <strong>Add</strong> the student?' : 'Are you sure you want to <strong>Update</strong> the student?',title: isNew ? 'Add the Student' : 'Update the Student'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.updateCourseStudentData();
        this.saveData();
      }
    })
  }

  saveCourseStudent() {            
    if (this.courseStudentData.testScore !== null && this.courseStudentData.testScore.toString().trim() === "") {
      this.courseStudentData.testScore = null;
    }    
    
    const saveService$ = this.isProcessed 
      ? this.atpService.saveATPCourseStudent(this.courseStudentData)
      : this.atpService.saveATPPendingCourseStudent(this.courseStudentData);
        
    saveService$.subscribe(result=>{
      this.toastr.success("Success")
      this.dialogRef.close(true);
    },error=>{
      this.toastr.error("There was an error saving the course student: ", error)
    })
  }

  saveData(){
    if (this.courseStudentData.testScore !== null && this.courseStudentData.testScore.toString().trim() !== '' && (isNaN(this.courseStudentData.testScore) || this.courseStudentData.testScore < 70 || this.courseStudentData.testScore > 100)) {
      this.toastr.error("The student's test score must be between 70 to 100 to save the student.");
    }
    else{
      if(this.isProcessed) {
        // For Processed students, save person and address separately
        this.personService.savePerson(this.selectedStudent).subscribe(result=>{
          this.selectedStudent = result;
          this.studentAddress.person = this.selectedStudent;
          this.courseStudentData['student'] = this.selectedStudent;
    
          this.commonService.saveAddress(this.address).subscribe(result=>{
            this.address = result;
            this.studentAddress.address = this.address;
    
            if(this.studentAddress.id === 0){
              this.personService.savePersonAddress(this.studentAddress).subscribe(result=>{},error=>{this.toastr.error("There was an error saving the person address: ", error);})
            }        
            this.saveCourseStudent();
          },error=>{this.toastr.error("There was an error saving the address: ", error);})
        },error=>{this.toastr.error("There was an error saving the student: ", error);})
      } else {
        // For Pending students, save directly with flat structure
        this.saveCourseStudent();
      }
    }
  }
    
  displayStudent(staffMember: any): string {
    if (staffMember) {
        if (staffMember.id === 0) {
            return staffMember.firstName;
        } else {
            return `${staffMember.id}: ${staffMember.firstName} ${staffMember.lastName}`;
        }
    } else {
        return '';
    }
  }
}

